import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import {
    AdminCity,
    AdminCityUserAccessRow,
    AdminUpdateUserRequest,
    AdminUser,
    AdminUserCityAccessPayload,
    AdminUserDetailResponse,
    AdminUserStatus
} from './admin-dashboard.models';
import { AdminDashboardService } from './admin-dashboard.service';
import { AdminDashboardStore } from './admin-dashboard.store';

type UserFormMode = 'idle' | 'create' | 'edit';
type ModelAccessKey = 'allAccess' | 't0Access' | 't3Access' | 't6Access' | 'dataUploadAccess';
type CityModelToggleKey = 't0Active' | 't3Active' | 't6Active' | 'dataUploadActive';

interface AdminUserFormCityAccess {
    cityId: string;
    cityName: string;
    countryName: string;
    allAccess: boolean;
    t0Access: boolean;
    t3Access: boolean;
    t6Access: boolean;
    dataUploadAccess: boolean;
}

interface AdminUserEditorForm {
    email: string;
    emailsRaw: string;
    status: AdminUserStatus;
    allAccess: boolean;
    selectAllCities: boolean;
    cityAccess: AdminUserFormCityAccess[];
}

interface AdminDraftAccess {
    allAccess: boolean;
    t0Access: boolean;
    t3Access: boolean;
    t6Access: boolean;
    dataUploadAccess: boolean;
}

@Component({
    selector: 'app-admin-dashboard',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, TooltipModule, DialogModule, ConfirmDialogModule],
    providers: [ConfirmationService],
    templateUrl: './admin-dashboard.component.html'
})
export class AdminDashboardComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly adminDashboardStore = inject(AdminDashboardStore);
    private readonly adminDashboardService = inject(AdminDashboardService);
    private readonly confirmationService = inject(ConfirmationService);
    private actionMessageTimer: number | null = null;

    protected readonly actionVisible = signal(false);
    protected citySearchTerm = '';
    protected cityPage = 1;
    protected readonly citiesPerPage = 6;
    protected userPage = 1;
    protected readonly usersPerPage = 6;
    protected cityUserSearchTerm = '';
    protected cityUserPage = 1;
    protected readonly cityUsersPerPage = 6;
    protected userSearchTerm = '';

    protected readonly cities = toSignal(this.adminDashboardStore.cities$, { initialValue: [] as AdminCity[] });
    protected readonly users = toSignal(this.adminDashboardStore.users$, { initialValue: [] as AdminUser[] });
    protected readonly metrics = toSignal(this.adminDashboardStore.metrics$, {
        initialValue: {
            totalUsers: 0,
            activeUsers: 0,
            inactiveUsers: 0,
            totalCitiesOnboarded: 0,
            activeCitiesT0: 0,
            activeCitiesT3: 0,
            activeCitiesT6: 0
        }
    });
    protected readonly selectedCity = toSignal(this.adminDashboardStore.selectedCity$, { initialValue: null as AdminCity | null });
    protected readonly selectedCityUsers = toSignal(this.adminDashboardStore.selectedCityUsers$, {
        initialValue: [] as AdminCityUserAccessRow[]
    });

    protected readonly formMode = signal<UserFormMode>('idle');
    protected readonly editorLoading = signal(false);
    protected readonly savingEditor = signal(false);
    protected readonly editingUserId = signal<string | null>(null);
    protected readonly cityDialogVisible = signal(false);
    protected readonly editorDialogVisible = signal(false);
    protected readonly duplicateEmails = signal<string[]>([]);
    protected readonly emailInputWarning = signal<string>('');

    // City-dialog draft (staged) permissions
    protected readonly cityUserDrafts = signal<Map<string, AdminCityUserAccessRow['permission']>>(new Map());
    protected readonly cityUsersDirty = signal(false);
    protected readonly savingCityUsers = signal(false);

    protected actionMessage = 'Admin dashboard ready.';
    protected actionTone: 'info' | 'success' = 'info';

    protected editorForm: AdminUserEditorForm = this.createEmptyEditorForm();
    protected editorCitySearchTerm = '';
    protected editorSelectedCityIds: string[] = [];
    protected editorDraftAccess: AdminDraftAccess = this.createEmptyDraftAccess();

    protected readonly summaryCards = computed(() => {
        const metrics = this.metrics();

        return [
            {
                label: 'Total Active Users',
                value: metrics.activeUsers,
                icon: 'pi pi-user-plus',
                borderClass: 'border-emerald-200',
                iconClass: 'bg-emerald-100 text-emerald-700'
            },
            {
                label: 'Cities onboarded',
                value: metrics.totalCitiesOnboarded,
                icon: 'pi pi-building',
                borderClass: 'border-violet-200',
                iconClass: 'bg-violet-100 text-violet-700'
            },
            {
                label: 'T0 Users',
                value: metrics.activeCitiesT0,
                icon: 'pi pi-bolt',
                borderClass: 'border-indigo-200',
                iconClass: 'bg-indigo-100 text-indigo-700'
            },
            {
                label: 'Enhanced Simulator Users',
                value: metrics.activeCitiesT3,
                icon: 'pi pi-chart-line',
                borderClass: 'border-pink-200',
                iconClass: 'bg-pink-100 text-pink-700'
            },
            {
                label: 'T6 Users',
                value: metrics.activeCitiesT6,
                icon: 'pi pi-globe',
                borderClass: 'border-teal-200',
                iconClass: 'bg-teal-100 text-teal-700'
            }
        ];
    });

    constructor() {
        // Keep drafts in sync with the store while no unsaved edits exist.
        effect(() => {
            const rows = this.selectedCityUsers();

            if (this.cityUsersDirty()) {
                return;
            }

            const map = new Map<string, AdminCityUserAccessRow['permission']>();
            rows.forEach((row) => map.set(row.userId, { ...row.permission }));
            this.cityUserDrafts.set(map);
        });

        this.destroyRef.onDestroy(() => {
            this.clearActionMessageTimer();
        });
    }

    ngOnInit(): void {
        this.adminDashboardStore.load();
    }

    // --- City list / selection ------------------------------------------------
    protected selectCity(city: AdminCity): void {
        this.adminDashboardStore.selectCity(city.id);
        this.cityUserSearchTerm = '';
        this.cityUserPage = 1;
        this.cityUsersDirty.set(false);
        this.cityDialogVisible.set(true);
    }

    protected closeCityDialog(): void {
        this.cityUserSearchTerm = '';
        this.cityUserPage = 1;
        this.cityUsersDirty.set(false);
        this.cityDialogVisible.set(false);
    }

    protected isSelectedCity(cityId: string): boolean {
        return this.selectedCity()?.id === cityId;
    }

    protected getDisplayName(email: string): string {
        return (email.split('@')[0] ?? '')
            .split(/[._-]+/)
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }

    // --- Editor open / close --------------------------------------------------
    protected startCreateUser(): void {
        this.formMode.set('create');
        this.editingUserId.set(null);
        this.duplicateEmails.set([]);
        this.editorForm = {
            email: '',
            emailsRaw: '',
            status: 'active',
            allAccess: false,
            selectAllCities: false,
            cityAccess: this.buildCityAccessForm()
        };
        this.resetEditorCityPicker();
        this.editorDialogVisible.set(true);
    }

    protected cancelEditor(): void {
        this.formMode.set('idle');
        this.editingUserId.set(null);
        this.duplicateEmails.set([]);
        this.editorForm = this.createEmptyEditorForm();
        this.resetEditorCityPicker();
        this.editorDialogVisible.set(false);
    }

    protected closeEditorDialog(): void {
        this.cancelEditor();
    }

    protected editUser(user: AdminUser): void {
        this.editorLoading.set(true);
        this.editorDialogVisible.set(true);

        this.adminDashboardService
            .getUserDetails(user.id)
            .pipe(
                finalize(() => this.editorLoading.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((response) => {
                this.formMode.set('edit');
                this.editingUserId.set(user.id);
                this.editorForm = this.mapUserDetailsToEditor(response);
                this.resetEditorCityPicker();
                this.showActionMessage(`Loaded ${this.getDisplayName(response.email)} for editing.`, 'info');
            });
    }

    // --- Save / delete --------------------------------------------------------
    protected saveUser(): void {
        const sharedCityAccess: AdminUserCityAccessPayload[] = this.editorForm.cityAccess
            .filter((city) => this.editorForm.allAccess || this.hasAnyAccess(city))
            .map((city) => ({
                cityId: city.cityId,
                cityName: city.cityName,
                allAccess: this.editorForm.allAccess ? true : city.allAccess,
                t0Access: this.editorForm.allAccess ? true : city.t0Access,
                t3Access: this.editorForm.allAccess ? true : city.t3Access,
                t6Access: this.editorForm.allAccess ? true : city.t6Access,
                dataUploadAccess: this.editorForm.allAccess ? true : city.dataUploadAccess
            }));

        if (!this.editorForm.allAccess && !sharedCityAccess.length) {
            this.showActionMessage('Select at least one city access option.', 'info');
            return;
        }

        if (this.formMode() === 'create') {
            const { valid, invalid } = this.parseEmails(this.editorForm.emailsRaw);

            if (invalid.length) {
                this.showActionMessage(`Invalid email(s): ${invalid.join(', ')}`, 'info');
                return;
            }

            if (!valid.length) {
                this.showActionMessage('Enter at least one valid email.', 'info');
                return;
            }

            this.onEmailsBlur();
            if (this.duplicateEmails().length) {
                this.showActionMessage(`Already onboarded: ${this.duplicateEmails().join(', ')}`, 'info');
                return;
            }

            this.savingEditor.set(true);
            this.adminDashboardService
                .onboardUsers(valid, {
                    status: this.editorForm.status,
                    adminAccess: 'no',
                    allAccess: this.editorForm.allAccess,
                    cityAccess: sharedCityAccess
                })
                .pipe(
                    finalize(() => this.savingEditor.set(false)),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((results) => {
                    const ok = results.filter((result) => result.success).length;
                    const failed = results.length - ok;
                    this.refreshDashboard(this.selectedCity()?.id ?? null);
                    this.showActionMessage(
                        failed ? `${ok} onboarded, ${failed} failed.` : `${ok} user(s) onboarded successfully.`,
                        failed ? 'info' : 'success'
                    );
                    this.cancelEditor();
                });

            return;
        }

        if (!this.editorForm.email.trim()) {
            this.showActionMessage('Email is required.', 'info');
            return;
        }

        const editingUserId = this.editingUserId();
        if (!editingUserId) {
            return;
        }

        const payload: AdminUpdateUserRequest = {
            action: 'update',
            fullName: this.getDisplayName(this.editorForm.email),
            email: this.editorForm.email.trim().toLowerCase(),
            status: this.editorForm.status,
            adminAccess: 'no',
            allAccess: this.editorForm.allAccess,
            cityAccess: sharedCityAccess
        };

        this.savingEditor.set(true);
        this.adminDashboardService
            .updateUser(editingUserId, payload)
            .pipe(
                finalize(() => this.savingEditor.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.refreshDashboard(this.selectedCity()?.id ?? null);
                this.showActionMessage('User updated successfully.', 'success');
                this.cancelEditor();
            });
    }

    protected confirmDeleteUser(event: Event, user: AdminUser): void {
        this.confirmationService.confirm({
            target: event.target as EventTarget,
            key: 'adminDeleteUser',
            header: 'Delete User',
            message: `Are you sure you want to delete ${this.getDisplayName(user.email)} (${user.email})? This action cannot be undone.`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger p-button-sm',
            rejectButtonStyleClass: 'p-button-text p-button-sm',
            accept: () => this.deleteUser(user)
        });
    }

    protected deleteUser(user: AdminUser): void {
        const payload: AdminUpdateUserRequest = {
            action: 'delete',
            fullName: this.getDisplayName(user.email),
            email: user.email,
            status: user.status,
            adminAccess: user.adminAccess,
            allAccess: false,
            cityAccess: user.permissions.map((permission) => ({
                cityId: permission.cityId,
                allAccess: permission.allAccess,
                t0Access: permission.t0Access,
                t3Access: permission.t3Access,
                t6Access: permission.t6Access,
                dataUploadAccess: permission.dataUploadAccess
            }))
        };

        this.adminDashboardService
            .updateUser(user.id, payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.refreshDashboard(this.selectedCity()?.id ?? null);
                this.showActionMessage(`${this.getDisplayName(user.email)} deleted successfully.`, 'success');

                if (this.editingUserId() === user.id) {
                    this.cancelEditor();
                }
            });
    }

    protected getCityUserCount(cityId: string): number {
        return this.users().filter((user) =>
            user.permissions.some((permission) => permission.cityId === cityId && this.hasAnyAccess(permission))
        ).length;
    }

    // --- Global / select-all toggles -----------------------------------------
    protected toggleEditorGlobalAccess(enabled: boolean): void {
        const nextCityAccess = this.editorForm.cityAccess.map((city) => ({
            ...city,
            allAccess: enabled,
            t0Access: enabled,
            t3Access: enabled,
            t6Access: enabled,
            dataUploadAccess: enabled
        }));

        this.editorForm = {
            ...this.editorForm,
            allAccess: enabled,
            selectAllCities: enabled,
            cityAccess: nextCityAccess
        };

        this.editorSelectedCityIds = [];
        this.editorDraftAccess = this.createEmptyDraftAccess();
    }

    protected toggleEditorSelectAllCities(enabled: boolean): void {
        if (this.editorForm.allAccess) {
            return;
        }

        const nextCityAccess = this.editorForm.cityAccess.map((city) => ({
            ...city,
            allAccess: enabled,
            t0Access: enabled,
            t3Access: enabled,
            t6Access: enabled,
            dataUploadAccess: enabled
        }));

        this.editorForm = {
            ...this.editorForm,
            selectAllCities: enabled,
            cityAccess: nextCityAccess
        };

        this.editorSelectedCityIds = [];
        this.editorDraftAccess = this.createEmptyDraftAccess();
    }

    // --- Left panel: city search + multi-select ------------------------------
    protected onEditorCitySearchChange(value: string): void {
        this.editorCitySearchTerm = value;
    }

    protected getFilteredEditorCities(): AdminUserFormCityAccess[] {
        const query = this.editorCitySearchTerm.trim().toLowerCase();

        if (!query) {
            return this.editorForm.cityAccess;
        }

        return this.editorForm.cityAccess.filter((city) =>
            `${city.cityName} ${city.countryName}`.toLowerCase().includes(query)
        );
    }

    protected selectEditorCity(cityId: string): void {
        if (this.editorForm.allAccess) {
            return;
        }

        if (this.editorSelectedCityIds.includes(cityId)) {
            this.editorSelectedCityIds = this.editorSelectedCityIds.filter((id) => id !== cityId);
        } else {
            this.editorSelectedCityIds = [...this.editorSelectedCityIds, cityId];
        }
    }

    protected isEditorCitySelected(cityId: string): boolean {
        return this.editorSelectedCityIds.includes(cityId);
    }

    protected isEditorCityAssigned(city: AdminUserFormCityAccess): boolean {
        return this.hasAnyAccess(city);
    }

    protected hasSelectedEditorCities(): boolean {
        return this.editorSelectedCityIds.length > 0;
    }

    protected getSelectedEditorCities(): AdminUserFormCityAccess[] {
        return this.editorSelectedCityIds
            .map((id) => this.editorForm.cityAccess.find((city) => city.cityId === id))
            .filter((city): city is AdminUserFormCityAccess => !!city);
    }

    protected removeSelectedEditorCity(cityId: string): void {
        this.editorSelectedCityIds = this.editorSelectedCityIds.filter((id) => id !== cityId);
    }

    // --- Right panel: draft access -------------------------------------------
    protected isDraftAccessActive(key: ModelAccessKey): boolean {
        return this.editorDraftAccess[key];
    }

    protected toggleDraftAccess(key: ModelAccessKey): void {
        if (this.editorForm.allAccess) {
            return;
        }

        if (key === 'allAccess') {
            const nextValue = !this.editorDraftAccess.allAccess;
            this.editorDraftAccess = {
                allAccess: nextValue,
                t0Access: nextValue,
                t3Access: nextValue,
                t6Access: nextValue,
                dataUploadAccess: nextValue
            };
            return;
        }

        const nextDraft: AdminDraftAccess = {
            ...this.editorDraftAccess,
            [key]: !this.editorDraftAccess[key]
        };

        nextDraft.allAccess =
            nextDraft.t0Access && nextDraft.t3Access && nextDraft.t6Access && nextDraft.dataUploadAccess;

        this.editorDraftAccess = nextDraft;
    }

    protected addSelectedCityAccess(): void {
        if (!this.editorSelectedCityIds.length) {
            this.showActionMessage('Select at least one city first.', 'info');
            return;
        }

        if (!this.hasAnyDraftAccess()) {
            this.showActionMessage('Select at least one access option for the selected cities.', 'info');
            return;
        }

        const selected = new Set(this.editorSelectedCityIds);
        const draft = this.editorDraftAccess;

        const nextCityAccess = this.editorForm.cityAccess.map((city) =>
            selected.has(city.cityId)
                ? {
                      ...city,
                      allAccess: draft.allAccess,
                      t0Access: draft.t0Access,
                      t3Access: draft.t3Access,
                      t6Access: draft.t6Access,
                      dataUploadAccess: draft.dataUploadAccess
                  }
                : city
        );

        this.editorForm = {
            ...this.editorForm,
            selectAllCities: this.areAllCityOptionsSelected(nextCityAccess),
            cityAccess: nextCityAccess
        };

        const count = this.editorSelectedCityIds.length;
        this.editorSelectedCityIds = [];
        this.editorDraftAccess = this.createEmptyDraftAccess();
        this.showActionMessage(`Access applied to ${count} city(ies).`, 'success');
    }

    protected getDraftActionLabel(): string {
        const count = this.editorSelectedCityIds.length;
        return count > 1 ? `Provide access to ${count} cities` : 'Provide City Access';
    }

    // --- Assigned cities table ------------------------------------------------
    protected editAssignedCity(cityId: string): void {
        const city = this.editorForm.cityAccess.find((item) => item.cityId === cityId);

        if (!city) {
            return;
        }

        this.editorSelectedCityIds = [cityId];
        this.editorDraftAccess = {
            allAccess: city.allAccess,
            t0Access: city.t0Access,
            t3Access: city.t3Access,
            t6Access: city.t6Access,
            dataUploadAccess: city.dataUploadAccess
        };
    }

    protected removeAssignedCity(cityId: string): void {
        const nextCityAccess = this.editorForm.cityAccess.map((city) =>
            city.cityId === cityId
                ? {
                      ...city,
                      allAccess: false,
                      t0Access: false,
                      t3Access: false,
                      t6Access: false,
                      dataUploadAccess: false
                  }
                : city
        );

        this.editorForm = {
            ...this.editorForm,
            selectAllCities: false,
            cityAccess: nextCityAccess
        };

        this.editorSelectedCityIds = this.editorSelectedCityIds.filter((id) => id !== cityId);
    }

    protected getAssignedEditorCities(): AdminUserFormCityAccess[] {
        return this.editorForm.cityAccess.filter((city) => this.hasAnyAccess(city));
    }

    protected getEditorAccessBadges(city: AdminUserFormCityAccess): string[] {
        if (city.allAccess) {
            return ['All'];
        }

        const labels: string[] = [];
        if (city.t0Access) labels.push('T0 Model');
        if (city.t3Access) labels.push('Enhanced Model');
        if (city.t6Access) labels.push('T6 Model');
        if (city.dataUploadAccess) labels.push('Data Upload');
        return labels;
    }

    protected getSelectedEditorCityCount(): number {
        if (this.editorForm.allAccess) {
            return this.editorForm.cityAccess.length;
        }

        return this.editorForm.cityAccess.filter((city) => this.hasAnyAccess(city)).length;
    }

    // --- City dialog: per-user access (staged/draft) -------------------------
    protected getDraftPermission(row: AdminCityUserAccessRow): AdminCityUserAccessRow['permission'] {
        return this.cityUserDrafts().get(row.userId) ?? row.permission;
    }

    protected updateAllAccess(row: AdminCityUserAccessRow, enabled: boolean): void {
        const map = new Map(this.cityUserDrafts());
        const current = map.get(row.userId) ?? { ...row.permission };

        map.set(row.userId, {
            ...current,
            allAccess: enabled,
            t0Access: enabled ? true : current.t0Access,
            t3Access: enabled ? true : current.t3Access,
            t6Access: enabled ? true : current.t6Access,
            dataUploadAccess: enabled ? true : current.dataUploadAccess
        });

        this.cityUserDrafts.set(map);
        this.cityUsersDirty.set(true);
    }

    protected updateTierAccess(
        row: AdminCityUserAccessRow,
        tier: 't0Access' | 't3Access' | 't6Access' | 'dataUploadAccess',
        enabled: boolean
    ): void {
        const map = new Map(this.cityUserDrafts());
        const current = map.get(row.userId) ?? { ...row.permission };

        map.set(row.userId, {
            ...current,
            allAccess: false,
            [tier]: enabled
        });

        this.cityUserDrafts.set(map);
        this.cityUsersDirty.set(true);
    }

    protected applyCityUserChanges(): void {
        const selectedCity = this.selectedCity();

        if (!selectedCity || !this.cityUsersDirty()) {
            return;
        }

        const drafts = this.cityUserDrafts();
        this.savingCityUsers.set(true);

        drafts.forEach((permission, userId) => {
            this.adminDashboardStore.updateCityPermission(selectedCity.id, userId, { ...permission });
        });

        this.cityUsersDirty.set(false);
        this.savingCityUsers.set(false);
        this.showActionMessage('User access updated successfully.', 'success');
    }

    protected cancelCityUserChanges(): void {
        const map = new Map<string, AdminCityUserAccessRow['permission']>();
        this.selectedCityUsers().forEach((row) => map.set(row.userId, { ...row.permission }));

        this.cityUserDrafts.set(map);
        this.cityUsersDirty.set(false);
    }

    // --- Action toast ---------------------------------------------------------
    protected dismissActionMessage(): void {
        this.actionVisible.set(false);
        this.clearActionMessageTimer();
    }

    // --- Cities table pagination ---------------------------------------------
    protected onCitySearchChange(value: string): void {
        this.citySearchTerm = value;
        this.cityPage = 1;
    }

    protected getVisibleCities(): AdminCity[] {
        const filteredCities = this.getFilteredCities();
        const startIndex = (this.cityPage - 1) * this.citiesPerPage;
        return filteredCities.slice(startIndex, startIndex + this.citiesPerPage);
    }

    protected getCityPageNumbers(): number[] {
        return Array.from({ length: this.getTotalCityPages() }, (_, index) => index + 1);
    }

    protected goToCityPage(page: number): void {
        this.cityPage = Math.min(Math.max(page, 1), this.getTotalCityPages());
    }

    // --- Users table pagination ----------------------------------------------
    protected onUserSearchChange(value: string): void {
        this.userSearchTerm = value;
        this.userPage = 1;
    }

    protected getFilteredUsers(): AdminUser[] {
        const query = this.userSearchTerm.trim().toLowerCase();

        if (!query) {
            return this.users();
        }

        return this.users().filter((user) =>
            `${this.getDisplayName(user.email)} ${user.email} ${user.status}`.toLowerCase().includes(query)
        );
    }

    protected getVisibleUsers(): AdminUser[] {
        const filteredUsers = this.getFilteredUsers();
        const startIndex = (this.userPage - 1) * this.usersPerPage;
        return filteredUsers.slice(startIndex, startIndex + this.usersPerPage);
    }

    protected getUserPageNumbers(): number[] {
        return Array.from({ length: this.getTotalUserPages() }, (_, index) => index + 1);
    }

    protected goToUserPage(page: number): void {
        this.userPage = Math.min(Math.max(page, 1), this.getTotalUserPages());
    }

    // --- City dialog pagination ----------------------------------------------
    protected onCityUserSearchChange(value: string): void {
        this.cityUserSearchTerm = value;
        this.cityUserPage = 1;
    }

    protected get filteredSelectedCityUsers(): AdminCityUserAccessRow[] {
        const query = this.cityUserSearchTerm.trim().toLowerCase();

        if (!query) {
            return this.selectedCityUsers();
        }

        return this.selectedCityUsers().filter((row) =>
            `${this.getDisplayName(row.email)} ${row.email} ${row.status}`.toLowerCase().includes(query)
        );
    }

    protected getVisibleSelectedCityUsers(): AdminCityUserAccessRow[] {
        const startIndex = (this.cityUserPage - 1) * this.cityUsersPerPage;
        return this.filteredSelectedCityUsers.slice(startIndex, startIndex + this.cityUsersPerPage);
    }

    protected getSelectedCityUserPageNumbers(): number[] {
        return Array.from({ length: this.getTotalSelectedCityUserPages() }, (_, index) => index + 1);
    }

    protected goToSelectedCityUserPage(page: number): void {
        this.cityUserPage = Math.min(Math.max(page, 1), this.getTotalSelectedCityUserPages());
    }

    // --- City model toggle (no persistence endpoint; local only) -------------
    protected toggleCityModel(city: AdminCity, key: CityModelToggleKey): void {
        const nextValue = !city[key];
        const store = this.adminDashboardStore as any;
        const service = this.adminDashboardService as any;

        const request =
            store.updateCityModel?.(city.id, { [key]: nextValue }) ??
            store.toggleCityModel?.(city.id, key, nextValue) ??
            service.updateCityModel?.(city.id, { [key]: nextValue });

        if (request?.pipe) {
            request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                this.refreshDashboard(this.selectedCity()?.id ?? null);
            });
            return;
        }

        (city as any)[key] = nextValue;
    }

    // --- Users table helpers --------------------------------------------------
    protected getAssignedCitiesTooltip(user: AdminUser): string {
        if (!user.permissions.length) {
            return 'No cities assigned';
        }

        return user.permissions
            .filter((permission) => this.hasAnyAccess(permission))
            .map((permission) => {
                const cityName = this.cities().find((city) => city.id === permission.cityId)?.name ?? permission.cityId;
                return `${cityName}: ${this.getAccessLabel(permission)}`;
            })
            .join(' | ');
    }

    protected getSelectedCityCount(user: AdminUser): number {
        return user.permissions.filter((permission) => this.hasAnyAccess(permission)).length;
    }

    protected setEditorStatus(status: AdminUserStatus): void {
        this.editorForm = { ...this.editorForm, status };
    }

    // --- Email parsing (bulk create) -----------------------------------------
    protected parsedEmailCount(): number {
        return this.parseEmails(this.editorForm.emailsRaw).valid.length;
    }

    protected onEmailKeydown(event: KeyboardEvent): void {
        if (event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }

        const controlKeys = [
            'Backspace',
            'Tab',
            'Enter',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End'
        ];

        if (controlKeys.includes(event.key)) {
            return;
        }

        if (event.key.length === 1 && !this.isAllowedEmailChar(event.key)) {
            event.preventDefault();
            this.emailInputWarning.set(`"${event.key}" is not allowed in an email address.`);
        } else {
            this.emailInputWarning.set('');
        }
    }

    protected onEmailPaste(event: ClipboardEvent): void {
        const pasted = event.clipboardData?.getData('text') ?? '';
        const sanitized = pasted
            .split('')
            .filter((char) => this.isAllowedEmailChar(char))
            .join('');

        if (sanitized !== pasted) {
            event.preventDefault();

            const target = event.target as HTMLTextAreaElement;
            const start = target.selectionStart ?? target.value.length;
            const end = target.selectionEnd ?? target.value.length;
            const nextValue = target.value.slice(0, start) + sanitized + target.value.slice(end);

            this.editorForm = { ...this.editorForm, emailsRaw: nextValue };
            this.onEmailsBlur();
            this.emailInputWarning.set('Removed unsupported characters from the pasted text.');
        } else {
            this.emailInputWarning.set('');
        }
    }

    protected onEmailsBlur(): void {
        const { valid } = this.parseEmails(this.editorForm.emailsRaw);
        const existing = new Set(this.users().map((user) => user.email.trim().toLowerCase()));
        this.duplicateEmails.set(valid.filter((email) => existing.has(email)));
    }

    protected invalidEmails(): string[] {
        return this.parseEmails(this.editorForm.emailsRaw).invalid;
    }

    private isAllowedEmailChar(char: string): boolean {
        return /[A-Za-z0-9@._+\-,;\s]/.test(char);
    }

    private parseEmails(raw: string): { valid: string[]; invalid: string[] } {
        const tokens = (raw || '')
            .split(/[\s,;]+/)
            .map((token) => token.trim().toLowerCase())
            .filter(Boolean);

        const seen = new Set<string>();
        const valid: string[] = [];
        const invalid: string[] = [];
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        for (const token of tokens) {
            if (seen.has(token)) {
                continue;
            }
            seen.add(token);
            (pattern.test(token) ? valid : invalid).push(token);
        }

        return { valid, invalid };
    }

    // --- Private helpers ------------------------------------------------------
    private refreshDashboard(selectedCityId: string | null): void {
        this.adminDashboardStore.load();

        if (selectedCityId) {
            window.setTimeout(() => {
                this.adminDashboardStore.selectCity(selectedCityId);
            }, 250);
        }
    }

    private mapUserDetailsToEditor(response: AdminUserDetailResponse): AdminUserEditorForm {
        const cityAccessMap = new Map(response.cityAccess.map((item) => [item.cityId, item]));
        const allAccess =
            response.allAccess === true ||
            (this.cities().length > 0 &&
                this.cities().every((city) => {
                    const saved = cityAccessMap.get(city.id);
                    return !!saved &&
                        (saved.allAccess ||
                            (saved.t0Access && saved.t3Access && saved.t6Access && saved.dataUploadAccess));
                }));

        return {
            email: response.email,
            emailsRaw: '',
            status: response.status,
            allAccess,
            selectAllCities: allAccess,
            cityAccess: this.cities().map((city) => {
                const saved = cityAccessMap.get(city.id);

                return {
                    cityId: city.id,
                    cityName: city.name,
                    countryName: city.countryName,
                    allAccess: allAccess ? true : (saved?.allAccess ?? false),
                    t0Access: allAccess ? true : (saved?.t0Access ?? false),
                    t3Access: allAccess ? true : (saved?.t3Access ?? false),
                    t6Access: allAccess ? true : (saved?.t6Access ?? false),
                    dataUploadAccess: allAccess ? true : (saved?.dataUploadAccess ?? false)
                };
            })
        };
    }

    private buildCityAccessForm(): AdminUserFormCityAccess[] {
        return this.cities().map((city) => ({
            cityId: city.id,
            cityName: city.name,
            countryName: city.countryName,
            allAccess: false,
            t0Access: false,
            t3Access: false,
            t6Access: false,
            dataUploadAccess: false
        }));
    }

    private createEmptyEditorForm(): AdminUserEditorForm {
        return {
            email: '',
            emailsRaw: '',
            status: 'active',
            allAccess: false,
            selectAllCities: false,
            cityAccess: []
        };
    }

    private createEmptyDraftAccess(): AdminDraftAccess {
        return {
            allAccess: false,
            t0Access: false,
            t3Access: false,
            t6Access: false,
            dataUploadAccess: false
        };
    }

    private hasAnyDraftAccess(): boolean {
        const draft = this.editorDraftAccess;
        return draft.allAccess || draft.t0Access || draft.t3Access || draft.t6Access || draft.dataUploadAccess;
    }

    private hasAnyAccess(city: AdminUserFormCityAccess | AdminUserCityAccessPayload): boolean {
        return city.allAccess || city.t0Access || city.t3Access || city.t6Access || city.dataUploadAccess;
    }

    private getAccessLabel(permission: AdminUserCityAccessPayload): string {
        if (permission.allAccess) {
            return 'All';
        }

        const labels: string[] = [];
        if (permission.t0Access) labels.push('T0 Model');
        if (permission.t3Access) labels.push('Enhanced Model');
        if (permission.t6Access) labels.push('T6 Model');
        if (permission.dataUploadAccess) labels.push('Data Upload');
        return labels.join(', ') || 'None';
    }

    private areAllCityOptionsSelected(cityAccess: AdminUserFormCityAccess[]): boolean {
        return (
            cityAccess.length > 0 &&
            cityAccess.every(
                (city) =>
                    city.allAccess ||
                    (city.t0Access && city.t3Access && city.t6Access && city.dataUploadAccess)
            )
        );
    }

    private resetEditorCityPicker(): void {
        this.editorCitySearchTerm = '';
        this.editorSelectedCityIds = [];
        this.editorDraftAccess = this.createEmptyDraftAccess();
        this.emailInputWarning.set('');
    }

    private showActionMessage(message: string, tone: 'info' | 'success' = 'info'): void {
        this.actionMessage = message;
        this.actionTone = tone;
        this.actionVisible.set(true);

        this.clearActionMessageTimer();
        this.actionMessageTimer = window.setTimeout(() => {
            this.actionVisible.set(false);
            this.actionMessageTimer = null;
        }, 4000);
    }

    private clearActionMessageTimer(): void {
        if (this.actionMessageTimer !== null) {
            window.clearTimeout(this.actionMessageTimer);
            this.actionMessageTimer = null;
        }
    }

    private getFilteredCities(): AdminCity[] {
        const query = this.citySearchTerm.trim().toLowerCase();

        if (!query) {
            return this.cities();
        }

        return this.cities().filter((city) =>
            `${city.name} ${city.countryName} ${city.citySpecification}`.toLowerCase().includes(query)
        );
    }

    private getTotalCityPages(): number {
        return Math.max(1, Math.ceil(this.getFilteredCities().length / this.citiesPerPage));
    }

    private getTotalUserPages(): number {
        return Math.max(1, Math.ceil(this.getFilteredUsers().length / this.usersPerPage));
    }

    private getTotalSelectedCityUserPages(): number {
        return Math.max(1, Math.ceil(this.filteredSelectedCityUsers.length / this.cityUsersPerPage));
    }
}
